import { A } from '@adornis/base/env-info.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { GlobalMailTemplate, MailTemplateVars } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import {
  type BuildProductChanges,
  buildBookableWorkshopsHTML,
  buildConfirmMail,
  buildForgotPassword,
  buildLoginLink,
  buildNewsletterOptinLink,
  buildOTP,
  buildProductCard,
  buildProductChangesHtml,
  buildRegistrationsHTML,
} from '@adornis/buchungsheld-db/mails/mail-helpers.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { XMailEditor } from '@adornis/mails/client/x-mail-editor-legacy.js';
import { getMailTemplateByType } from '@adornis/mails/db/mail-template.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { add, addDays } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

const mockAppointments = [
  { _id: '1', name: 'Introduction', start: addDays(new Date(), 1), end: addDays(new Date(), 3), location: 'Uniplatz' },
  {
    _id: '2',
    name: 'Introduction 2',
    start: add(new Date(), { days: 8, hours: 5 }),
    end: add(new Date(), { days: 8, hours: 12 }),
    location: 'Hoersaal',
  },
];

/**
 * @element tr-mail-editor
 */
@customElement('tr-mail-editor')
export class TrMailEditor extends XMailEditor {
  @property({ type: Boolean }) overriding?: boolean;
  @property({ type: Boolean, attribute: 'send-only' }) sendOnly?: boolean;
  @property() contextVariables?: Record<string, string>;

  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  protected editorOptions = {
    toolbar: this.tinyEditorOptions,
    relative_urls: false,
    remove_script_host: false,
    images_upload_handler: async (blobInfo, progress): Promise<string> => {
      const blob = blobInfo.blob();
      const id = await ImageFile.upload(
        new File([blob], blobInfo.filename(), { lastModified: blob.lastModified, type: blob.type }),
      );
      return A.absoluteUrl(ImageFile.getServeLink(id));
    },
  };

  override async getVariableValues() {
    const mailTemplate = this.value.value;
    if (!mailTemplate) return {};
    const superVarValues = await super.getVariableValues();

    if (this.contextVariables)
      return Object.keys(superVarValues).reduce((acc, key) => ({ ...acc, [key]: this.contextVariables?.[key] }), {});

    const changedRegistrationsValue = buildRegistrationsHTML(
      [
        {
          name: 'Workshop Name',
          state: ParticipationState.waiting,
          topic: 'Test Theme',
          language: 'de',
          waitingListPlace: 2,
          appointments: mockAppointments,
        },
      ],
      true,
    );

    const changedRegistrationsEnglishValue = buildRegistrationsHTML(
      [
        {
          name: 'Workshop Name',
          state: ParticipationState.waiting,
          topic: 'Test Theme',
          language: 'de',
          waitingListPlace: 2,
          appointments: mockAppointments,
          englishVersion: true,
        },
      ],
      true,
    );

    const oldWorkshop = new Workshop({
      name: 'Workshop 1',
      capacity: 10,
      infoLink: 'https://google.com',
      appointments: [
        { _id: 'appointment1', name: 'Termin 1', start: new Date(), end: new Date(), location: 'Ort' },
        { _id: 'appointment2', name: 'Termin 2', start: new Date(), end: new Date(), location: 'Ort 2' },
      ],
    });

    const newWorkshop = {
      name: { old: 'Alter Titel', new: 'Neuer Titel' },
      teachings: {
        old: [{ title: 'alter Dozent', firstName: 'alter', lastName: 'Dozent' }],
        new: [{ title: 'neuer Dozent', firstName: 'neuer', lastName: 'Dozent' }],
      },
      appointments: [
        {
          old: { _id: 'appointment1', location: 'Alter Ort', start: new Date() },
          new: { _id: 'appointment1', location: 'Neuer Ort', start: addDays(new Date(), 2) },
        },
        { old: {}, new: { _id: 'appointment2', name: 'Termin 2' } },
        {
          new: {},
          old: { _id: 'appointment3', name: 'Termin 3', start: new Date(), end: new Date(), location: 'Ort' },
        },
      ],
    } satisfies BuildProductChanges;

    return {
      ...superVarValues,
      ...Object.fromEntries(
        [
          [MailTemplateVars.FORGOT_PASSWORD_LINK, buildForgotPassword('https://www.google.de')] as const,
          [MailTemplateVars.CONFIRM_MAIL_LINK, buildConfirmMail('https://www.google.de')] as const,
          [MailTemplateVars.OTP, buildOTP('2456')] as const,
          [MailTemplateVars.LOGIN_LINK, buildLoginLink('1298578215712895719282571')] as const,
          [
            MailTemplateVars.PRODUCT_CARD,
            buildProductCard('Workshop Name', new Date(), undefined, false, 'Hörsaal 2'),
          ] as const,
          [
            MailTemplateVars.PRODUCT_CARD_EN,
            buildProductCard('Workshop Name', new Date(), undefined, false, 'Hörsaal 2', 'en'),
          ] as const,
          [MailTemplateVars.EVENT_LOCATION, 'Musterstraße 42, 123456 Musterort'] as const,
          [MailTemplateVars.EVENT_DATES, '2025-03-26, 2025-03-26'] as const,
          [MailTemplateVars.EVENT_LEAD_DAYS, '5'] as const,
          [MailTemplateVars.PRODUCT_NAME, 'Workshop Name'] as const,
          [MailTemplateVars.WORKSHOP_NAME, 'Workshop Name'] as const,
          [
            MailTemplateVars.WORKSHOP_TERMINE,
            new TimeSlot({ start: add(new Date(), { days: 10 }), end: add(new Date(), { days: 10, hours: 2 }) }),
          ] as const,
          [MailTemplateVars.WORKSHOP_LOCATION, 'Location1, Location2'] as const,
          [MailTemplateVars.APPOINTMENT_NAME, 'Termin 1'] as const,
          [MailTemplateVars.APPOINTMENT_START, add(new Date(), { days: 10 })] as const,
          [MailTemplateVars.APPOINTMENT_END, add(new Date(), { days: 10, hours: 2 })] as const,
          [MailTemplateVars.APPOINTMENT_LOCATION, 'Sachsenhügel Straße 42'] as const,
          [MailTemplateVars.CHANGED_REGISTRATIONS, changedRegistrationsValue] as const,
          [MailTemplateVars.CHANGED_REGISTRATIONS_EN, changedRegistrationsEnglishValue] as const,
          [MailTemplateVars.EVENT_TITLE, 'Hallo-Welt Veranstaltung'] as const,
          [
            MailTemplateVars.DOCUMENT_LINKS,
            '<a href="https://www.google.de">Link</a>, <a href="https://www.google.de">Link</a>',
          ] as const,
          [
            MailTemplateVars.OTHER_REGISTRATIONS,
            buildRegistrationsHTML(
              [...new Array(3)].map((_, i) => ({
                name: `Workshop ${i + 1}`,
                state: ParticipationState.in,
                topic: 'Test Theme',
                language: 'de',
                dates: [new Date()],
                institution: new Institution({}),
                appointments: mockAppointments,
              })),
            ),
          ] as const,
          [MailTemplateVars.PRODUCT_CHANGES, buildProductChangesHtml(oldWorkshop, newWorkshop, false)] as const,
          [MailTemplateVars.PRODUCT_CHANGES_EN, buildProductChangesHtml(oldWorkshop, newWorkshop, true)] as const,
          [
            MailTemplateVars.BOOKABLE_WORKSHOPS,
            buildBookableWorkshopsHTML(
              [1, 2, 3, 4, 5].map(
                i =>
                  new Workshop({
                    _id: 'testing_product_1',
                    institutionID: 'testing_institution_1',
                    name: 'Workshop ' + i,
                    appointments: [
                      {
                        name: 'Termin 1',
                        start: addDays(new Date(), i),
                        end: add(new Date(), { days: i, hours: 3 }),
                        location: 'online',
                      },
                    ],
                    Language: 'de',
                  }),
              ),
              false,
            ),
          ] as const,
          [
            MailTemplateVars.BOOKABLE_WORKSHOPS_EN,
            buildBookableWorkshopsHTML(
              [1, 2, 3, 4, 5].map(
                i =>
                  new Workshop({
                    _id: 'testing_product_1',
                    institutionID: 'testing_institution_1',
                    name: 'Workshop ' + i,
                    appointments: [
                      {
                        name: 'Termin 1',
                        start: addDays(new Date(), i),
                        end: add(new Date(), { days: i, hours: 3 }),
                        location: 'online',
                      },
                    ],
                    Language: 'en',
                  }),
              ),
              true,
            ),
          ] as const,
          [MailTemplateVars.NEWSLETTER_OPTIN_LINK, buildNewsletterOptinLink('preview-token-123', 'de')] as const,
          [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN, buildNewsletterOptinLink('preview-token-123', 'en')] as const,
        ].filter(([key]) => key in superVarValues),
      ),
    };
  }

  protected override writerContent() {
    return xComponents(html`
      <x-flex space="md" flex>
        <x-h3> Nachricht </x-h3>
        ${this.variablesDisplay()}
        <x-input ${this._form.field('subject')} placeholder="Betreff"></x-input>
        <x-tinymce
          flex
          placeholder="Hier E-Mail Text eingeben.."
          .editorOptions=${this.editorOptions}
          ${this._form.field('content')}
        ></x-tinymce>
      </x-flex>
    `);
  }

  protected override saveButton() {
    return this.sendOnly ? nothing : super.saveButton();
  }

  protected override deleteButton() {
    return this.sendOnly ? nothing : super.deleteButton();
  }

  override render() {
    return html`
      <tr-flex space="lg" flex h100>
        <tr-flex horizontal space="md" wrap flex> ${this.editorContent()} ${this.previewContent()} </tr-flex>
        ${this.attachments ? this.attachmentSection() : nothing}
        <tr-flex start horizontal space="md">
          ${this.saveButton()} ${this.deleteButton()} ${this._copyFromGlobal()}
        </tr-flex>
      </tr-flex>
      ${this.attachmentSection()}
    `;
  }

  protected _copyFromGlobal() {
    if (this.sendOnly) return nothing;
    if (!this.overriding) return nothing;
    const mailTemplate = this.value.value;
    if (!mailTemplate) return nothing;
    return html`
      <x-button
        mode="outline"
        icon="content_copy"
        @click=${() =>
          XDialog.guard(
            async () => {
              const globalMailTemplate = await getMailTemplateByType(mailTemplate.emailType)(
                selectionSet(() => GlobalMailTemplate, 2),
              );
              if (!globalMailTemplate) return;
              mailTemplate.subject = globalMailTemplate.subject;
              mailTemplate.content = globalMailTemplate.content;
            },
            {
              hasToConfirm: true,
              confirmText:
                'Wirklich lokale E-Mail-Vorlage mit globaler überschreiben? Die lokalen Änderungen gehen dabei verloren. Um die Änderungen zu persistieren, müssen Sie danach speichern.',
            },
          )}
      >
        ${this._translation.translate('MAIL_EDITOR', 'copy_from_template')}
      </x-button>
    `;
  }
}
